import React, {
  useEffect, useMemo, useState,
} from 'react';

import Close from '@mui/icons-material/Close';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import {
  Alert,
  Grid, IconButton, Stack, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';

import Button from '../../../components/Button';
import CommitForm from '../../../components/CommitForm';
import ContentCard from '../../../components/ContentCard';
import ContentGroupsList from '../../../components/ContentGroupsList';
import Modal from '../../../components/Modal';
import SkeletonStack from '../../../components/SkeletonStack';
import {
  useGetQuestionQuery, useRemoveQuestionMutation, useUpdateQuestionMutation,
} from '../../../services/exams';
import { Exam } from '../../../services/exams/types';
import { useGetTopicsQuery } from '../../../services/topics';
import { Topic } from '../../../services/topics/types';
import { QuestionType } from '../../../types/common';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';

interface QuestionDetailsProps {
  exam?: Exam,
  subject?: string,
  examId?: string,
  questionId?: string,
  onClose: () => void,
}

const QuestionDetails = ({ exam, subject, examId, questionId, onClose }: QuestionDetailsProps) => {
  const navigate = useNavigate();

  const [questionTopics, setQuestionTopics] = useState<Topic[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const { data: topics } = useGetTopicsQuery(
    { subject: exam?.subject || '', search: '' }, { skip: !exam?.subject },
  );
  const { data: questionData } = useGetQuestionQuery({
    subject: subject || '',
    examId: examId || '',
    questionId: questionId || '',
  }, { skip: !subject || !examId || !questionId });
  const [updateQuestion, {
    isLoading: isUpdateQuestionLoading,
    isError: isUpdateQuestionError,
    error: updateQuestionError,
  }] = useUpdateQuestionMutation();
  const [removeQuestion, {
    isSuccess: isRemoveQuestionSuccess,
    isLoading: isRemoveQuestionLoading,
    isError: isRemoveQuestionError,
    error: removeQuestionError,
  }] = useRemoveQuestionMutation();

  useEffect(() => {
    // Reset modal and close sidebar after a question is removed
    if (isRemoveQuestionSuccess) {
      setDeleteModalOpen(false);
      onClose();
    }
  }, [isRemoveQuestionSuccess]);

  useEffect(() => {
    if (questionData?.data.topics && topics?.data) {
      const detailedTopics = questionData.data.topics
        .map((x) => topics.data.find((y) => y.topicId === x))
        .filter((x) => x);
      setQuestionTopics(detailedTopics as Topic[]);
    }
  }, [questionData?.data.topics, topics?.data]);

  const isMCQ = useMemo(() =>
    questionData?.data.type === QuestionType.MultipleChoice,
  [questionData?.data.type]);

  useError(isUpdateQuestionError, updateQuestionError);
  useError(isRemoveQuestionError, removeQuestionError);
  useSuccess(isRemoveQuestionSuccess, 'Question deleted');

  if (!subject || !examId || !questionId || !exam) {
    return <SkeletonStack />;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
        >
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
          <Typography variant="h6">
            Question Details
          </Typography>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <ContentCard title="Question Details">
          <CommitForm
            data={{
              ...questionData?.data,
              ...(isMCQ && questionData?.data.mcqOpts?.answer ? {
                // converts 1 -> A, ...
                answer: String.fromCharCode(64 + questionData.data.mcqOpts.answer),
              } : {}),
            }}
            metadata={questionData?.metadata}
            fields={{
              type: {
                xs: 12,
              },
              label: {
                xs: 12,
              },
              maxMark: {
                xs: 12,
              },
              ...(isMCQ ? {
                answer: {
                  xs: 12,
                },
              } : {}),
            }}
            onSubmit={(values) => {
              updateQuestion({
                subject,
                examId,
                questionId,
                attributes: values,
              });
            }}
            isSubmitLoading={isUpdateQuestionLoading}
            autoSubmit
          />
        </ContentCard>
      </Grid>
      <Grid item xs={12}>
        <ContentCard title="Topics" variant="outlined">
          <Stack direction="column" spacing={2}>
            <Alert severity="warning">
              Question topics can only be modified at the time of creation.
            </Alert>
            <ContentGroupsList
              data={questionTopics}
              columns={[{
                field: 'label',
                headerName: 'Label',
                width: 300,
              }]}
              loading={!questionData}
              onRowClick={({ row }) => {
                navigate(`/admin/topics/${subject}/${row.topicGroup}`);
              }}
            />
          </Stack>
        </ContentCard>
      </Grid>
      <Grid item xs={12}>
        <ContentCard title="Actions" variant="outlined">
          <Button
            color="error"
            onClick={() => setDeleteModalOpen(true)}
            startIcon={(<DeleteRounded />)}
            disabled={exam?.analytics.uniqueAttempts > 0}
          >
            Delete
          </Button>
        </ContentCard>
      </Grid>
      <Modal
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(true)}
        title="Are you sure?"
        description="This will permanently delete this question."
        buttons={[
          {
            label: 'Delete',
            onClick: () => removeQuestion({
              subject,
              examId,
              questionId,
            }),
            color: 'error',
            loading: isRemoveQuestionLoading,
          },
        ]}
      />
    </Grid>
  );
};

export default QuestionDetails;
